import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import TrashIcon from "@ps-design-system/icons/Trash/Trash24";

import { TAX_DETAILS_STATUS } from "../TaxDetails.consts";
import type { TaxRegistration } from "../TaxDetails.types";
import { getTaxDetailsFormPath } from "../TaxDetails.utils";
import { useTaxDetails } from "../TaxDetailsProvider";

import { TAX_DETAILS_BANNER_MESSAGES } from "./TaxDetailsBanner.messages";
import { TaxDetailsBannerDescription } from "./TaxDetailsBannerDescription";
import { TaxDetailsBannerHeader } from "./TaxDetailsBannerHeader";
import { TaxDetailsBannerNote } from "./TaxDetailsBannerNote";

type TaxDetailsBannerProps = {
  registration: TaxRegistration;
};

/**
 * Renders one tax registration and its add / update / delete actions.
 */
const TaxDetailsBanner = ({ registration }: TaxDetailsBannerProps) => {
  const { openDeleteModal } = useTaxDetails();
  const { id, status } = registration;

  /**
   * Add and update open the same form for this registration's scope — whether
   * it is filled in already is the form's business, not the URL's.
   */
  const handleOpenForm = () => {
    location.href = getTaxDetailsFormPath(registration);
  };

  const handleDeleteDetails = () => {
    if (id) {
      openDeleteModal(id);
    }
  };

  return (
    <div className="tw-border tw-border-gray-300 tw-rounded-4 tw-p-16 tw-gap-16 tw-flex tw-flex-col">
      <div className="tw-gap-16 tw-flex tw-justify-between sm:tw-items-center tw-flex-col sm:tw-flex-row">
        <div className="tw-flex tw-flex-col tw-gap-16">
          <TaxDetailsBannerHeader registration={registration} />
          <TaxDetailsBannerDescription registration={registration} />
        </div>
        <div className="sm:tw-w-auto tw-w-full">
          {status === TAX_DETAILS_STATUS.empty ? (
            <Button
              onClick={handleOpenForm}
              appearance="purple"
              className="tw-w-full"
              testId="tax-details-banner-add"
            >
              <FormattedMessage {...TAX_DETAILS_BANNER_MESSAGES.addDetails} />
            </Button>
          ) : (
            <div className="tw-flex tw-justify-end tw-items-center tw-gap-4 tw-w-full">
              <Button
                onClick={handleOpenForm}
                disabled={status === TAX_DETAILS_STATUS.pending}
                variant="ghost"
              >
                <FormattedMessage
                  {...TAX_DETAILS_BANNER_MESSAGES.updateDetails}
                />
              </Button>
              <Button
                onClick={handleDeleteDetails}
                disabled={status === TAX_DETAILS_STATUS.pending}
                variant="ghost"
                iconOnly={true}
              >
                <TrashIcon />
                <span className="tw-sr-only">
                  <FormattedMessage
                    {...TAX_DETAILS_BANNER_MESSAGES.deleteDetails}
                  />
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
      <TaxDetailsBannerNote registration={registration} />
    </div>
  );
};

export { TaxDetailsBanner };
